import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { relativeDays } from "@/lib/campaignPlanning";
import { STEP_COPY } from "@/components/schools/setup/steps";
import { ProgressRing } from "@/components/schools/setup/SetupPrimitives";
import SetupTimeline from "@/components/schools/setup/SetupTimeline";

/**
 * The top of the setup guide: how far along the school is, what to do
 * next, and the runway to launch.
 */
export default function SetupOverview({ setup, activeKey, onSelect }) {
  const { progress, next_step: next, key_dates: dates, campaign } = setup;
  const { headline, detail } = summarize(setup);

  return (
    <Card className="px-5 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <ProgressRing complete={progress.complete} total={progress.total} size={44} stroke={4} />
          <div className="min-w-0">
            <p className="font-display text-lg font-semibold leading-tight text-foreground">{headline}</p>
            <p className="text-sm text-muted-foreground">{detail}</p>
          </div>
        </div>
        {next !== activeKey && (
          <Button size="sm" onClick={() => onSelect(next)} className="self-start sm:self-center">
            {next === "launch" ? "See launch checklist" : `Next up: ${STEP_COPY[next].short}`}
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {campaign?.dates_confirmed && dates.donations_open_on && <SetupTimeline keyDates={dates} className="mt-4" />}
    </Card>
  );
}

function summarize({ campaign, progress, key_dates: dates, ready_to_launch: ready }) {
  if (!campaign) {
    return {
      headline: "Welcome. We'll take this one step at a time.",
      detail: "Five stages, in the order that works best. Steps marked Required to launch have to be done; the rest are here because they make the read-a-thon go better, and you can skip any that don't fit your school.",
    };
  }

  // A signup draft's dates are placeholders until the school sets them.
  const countdown = !campaign.dates_confirmed ? ""
    : dates.today < dates.donations_open_on
    ? `Donations open ${relativeDays(dates.today, dates.donations_open_on)}.`
    : dates.today < dates.start_on
    ? `Reading starts ${relativeDays(dates.today, dates.start_on)}.`
    : "";

  if (progress.complete === progress.total) {
    return { headline: "Everything's done. You're ready for launch.", detail: countdown };
  }
  if (ready) {
    return {
      headline: `${progress.complete} of ${progress.total} done — ready for launch`,
      detail: `The required steps are finished, so ${campaign.name} will go live on its start date. ${countdown}`,
    };
  }
  return {
    headline: `${progress.complete} of ${progress.total} done`,
    detail: `${progress.required_remaining} required step${progress.required_remaining === 1 ? "" : "s"} left before ${campaign.name} can launch. ${countdown}`,
  };
}
